import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type SpotifyImage = {
  url: string;
  height: number | null;
  width: number | null;
};

type SpotifyUserProfile = {
  display_name: string;
  email?: string;
  id: string;
  product?: string;
  images?: SpotifyImage[];
};

type SpotifyPlaylist = {
  id: string;
  name: string;
  description: string;
  images?: SpotifyImage[];
  external_urls: {
    spotify: string;
  };
  tracks?: {
    total: number;
  };
  items?: {
    total: number;
  };
};

type SpotifyTrack = {
  id: string;
  uri: string;
  name: string;
  duration_ms: number;
  external_urls: {
    spotify: string;
  };
  album: {
    name: string;
    images: SpotifyImage[];
  };
  artists: Array<{
    id: string;
    name: string;
  }>;
};

type SavedTrackItem = {
  added_at: string;
  track: SpotifyTrack;
};

type PlaylistTrackItem = {
  added_at: string;
  track: SpotifyTrack | null;
};

type PlayerState = {
  paused?: boolean;
  position?: number;
  duration?: number;
  track_window?: {
    current_track?: {
      id?: string;
      name?: string;
      album?: {
        name?: string;
        images?: SpotifyImage[];
      };
      artists?: Array<{
        name?: string;
      }>;
    };
  };
};

type AuthStatusResponse = {
  authenticated: boolean;
};

type SpotifyStatusResponse = {
  connected: boolean;
  connectedAt: string | null;
};

type WebPlaybackTokenResponse = {
  accessToken: string;
};

type PlaylistsResponse = {
  items: SpotifyPlaylist[];
  total: number;
};

type LikedTracksResponse = {
  items: SavedTrackItem[];
};

type PlaylistTracksResponse = {
  items: PlaylistTrackItem[];
};

type SearchResponse = {
  tracks: {
    items: SpotifyTrack[];
  };
};

type OkResponse = {
  ok: boolean;
};

type SpotifyPlayerEventCallback = (data: any) => void;

interface SpotifyWebPlayerInstance {
  connect: () => Promise<boolean>;
  disconnect: () => void;
  addListener: (event: string, callback: SpotifyPlayerEventCallback) => boolean;
  removeListener: (
    event?: string,
    callback?: SpotifyPlayerEventCallback,
  ) => boolean;
  togglePlay: () => Promise<void>;
  nextTrack: () => Promise<void>;
  previousTrack: () => Promise<void>;
  pause: () => Promise<void>;
  resume: () => Promise<void>;
  seek: (positionMs: number) => Promise<void>;
  setVolume: (volume: number) => Promise<void>;
  getVolume: () => Promise<number>;
  getCurrentState: () => Promise<PlayerState | null>;
}

declare global {
  interface Window {
    Spotify?: {
      Player: new (options: {
        name: string;
        getOAuthToken: (callback: (token: string) => void) => void;
        volume?: number;
      }) => SpotifyWebPlayerInstance;
    };
    onSpotifyWebPlaybackSDKReady?: () => void;
  }
}

async function apiFetch<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: "include",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(options?.headers || {}),
    },
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Request fehlgeschlagen.");
  }

  return response.json() as Promise<T>;
}

async function loadSpotifySdk(): Promise<void> {
  if (window.Spotify) {
    return;
  }

  await new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(
      "spotify-player-sdk",
    ) as HTMLScriptElement | null;

    if (!existing) {
      const script = document.createElement("script");
      script.id = "spotify-player-sdk";
      script.src = "https://sdk.scdn.co/spotify-player.js";
      script.async = true;
      script.onerror = () =>
        reject(new Error("Spotify SDK konnte nicht geladen werden."));
      document.body.appendChild(script);
    }

    window.onSpotifyWebPlaybackSDKReady = () => resolve();
  });
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function formatTimeMs(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) {
    return "0:00";
  }

  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function getPlaylistTotal(playlist: SpotifyPlaylist): number {
  if (playlist.items?.total !== undefined) {
    return playlist.items.total;
  }

  if (playlist.tracks?.total !== undefined) {
    return playlist.tracks.total;
  }

  return 0;
}

function getArtistNames(track?: SpotifyTrack | null): string {
  if (!track?.artists?.length) {
    return "-";
  }

  return track.artists.map((artist) => artist.name).join(", ");
}

function IconHome() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="spotify-svg-icon">
      <path fill="currentColor" d="M12 3 4 9v11h6v-6h4v6h6V9l-8-6z" />
    </svg>
  );
}

function IconSearch() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="spotify-svg-icon">
      <path
        fill="currentColor"
        d="M10.5 4a6.5 6.5 0 1 0 4.03 11.6l4.44 4.44 1.41-1.41-4.44-4.44A6.5 6.5 0 0 0 10.5 4Zm0 2a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Z"
      />
    </svg>
  );
}

function IconLibrary() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="spotify-svg-icon">
      <path
        fill="currentColor"
        d="M4 4h2v16H4V4Zm4 2h2v14H8V6Zm4-2h8v2h-8V4Zm0 5h8v2h-8V9Zm0 5h8v2h-8v-2Zm0 5h8v2h-8v-2Z"
      />
    </svg>
  );
}

function IconPrevious() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="spotify-svg-icon">
      <path fill="currentColor" d="M6 6h2v12H6V6Zm3 6 9 6V6l-9 6Z" />
    </svg>
  );
}

function IconNext() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="spotify-svg-icon">
      <path fill="currentColor" d="M16 6h2v12h-2V6Zm-1 6-9 6V6l9 6Z" />
    </svg>
  );
}

function IconPause() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="spotify-svg-icon">
      <path fill="currentColor" d="M6 5h4v14H6V5Zm8 0h4v14h-4V5Z" />
    </svg>
  );
}

function IconPlay() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="spotify-svg-icon">
      <path fill="currentColor" d="M8 5v14l11-7L8 5Z" />
    </svg>
  );
}

function IconClock() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="spotify-svg-icon">
      <path
        fill="currentColor"
        d="M12 4a8 8 0 1 0 8 8 8.01 8.01 0 0 0-8-8Zm1 4h-2v5l4.2 2.52 1-1.72-3.2-1.9Z"
      />
    </svg>
  );
}

function SpotifyPage() {
  const [password, setPassword] = useState<string>("");
  const [appAuthenticated, setAppAuthenticated] = useState<boolean>(false);
  const [spotifyConnected, setSpotifyConnected] = useState<boolean>(false);

  const [profile, setProfile] = useState<SpotifyUserProfile | null>(null);
  const [playlists, setPlaylists] = useState<SpotifyPlaylist[]>([]);
  const [likedSongs, setLikedSongs] = useState<SavedTrackItem[]>([]);
  const [searchResults, setSearchResults] = useState<SpotifyTrack[]>([]);
  const [playlistTracks, setPlaylistTracks] = useState<SpotifyTrack[]>([]);

  const [selectedPlaylistId, setSelectedPlaylistId] = useState<string>("");
  const [selectedPlaylistName, setSelectedPlaylistName] =
    useState<string>("Lieblingssongs");
  const [contentMode, setContentMode] = useState<
    "search" | "playlist" | "liked"
  >("liked");

  const [query, setQuery] = useState<string>("daft punk");
  const [deviceId, setDeviceId] = useState<string>("");
  const [playerReady, setPlayerReady] = useState<boolean>(false);
  const [playerBooting, setPlayerBooting] = useState<boolean>(false);
  const [playerState, setPlayerState] = useState<PlayerState | null>(null);
  const [selectedTrackId, setSelectedTrackId] = useState<string>("");

  const [progressMs, setProgressMs] = useState<number>(0);
  const [progressDurationMs, setProgressDurationMs] = useState<number>(0);
  const [autoAdvanceLock, setAutoAdvanceLock] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(true);
  const [searching, setSearching] = useState<boolean>(false);
  const [playlistLoading, setPlaylistLoading] = useState<boolean>(false);
  const [authLoading, setAuthLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<string>("");
  const [error, setError] = useState<string>("");

  const playerRef = useRef<SpotifyWebPlayerInstance | null>(null);

  const currentTrack = playerState?.track_window?.current_track;
  const isPaused = playerState?.paused ?? true;
  const positionMs = progressMs;
  const durationMs = progressDurationMs || playerState?.duration || 0;
  const progressPercent =
    durationMs > 0
      ? Math.min(100, Math.max(0, (positionMs / durationMs) * 100))
      : 0;

  const displayedTracks = useMemo<SpotifyTrack[]>(() => {
    if (contentMode === "playlist") {
      return playlistTracks;
    }

    if (contentMode === "liked") {
      return likedSongs.map((item) => item.track);
    }

    return searchResults;
  }, [contentMode, likedSongs, playlistTracks, searchResults]);

  const selectedPlaylist = useMemo(() => {
    return (
      playlists.find((playlist) => playlist.id === selectedPlaylistId) || null
    );
  }, [playlists, selectedPlaylistId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const spotifyError = params.get("spotify_error");
    const spotifyConnectedFlag = params.get("spotify_connected");

    if (spotifyError) {
      setError(decodeURIComponent(spotifyError));
      window.history.replaceState({}, document.title, "/spotify");
    }

    if (spotifyConnectedFlag) {
      setStatus("Spotify ist verbunden.");
      window.history.replaceState({}, document.title, "/spotify");
    }
  }, []);

  useEffect(() => {
    const boot = async () => {
      try {
        const auth = await apiFetch<AuthStatusResponse>("/api/auth/status");
        setAppAuthenticated(auth.authenticated);

        if (auth.authenticated) {
          const spotifyStatus = await apiFetch<SpotifyStatusResponse>(
            "/api/spotify/status",
          );
          setSpotifyConnected(spotifyStatus.connected);
        }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Start fehlgeschlagen.";
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    void boot();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (!appAuthenticated || !spotifyConnected) {
        return;
      }

      try {
        setLoading(true);
        setError("");

        const [profileData, playlistData, likedData] = await Promise.all([
          apiFetch<SpotifyUserProfile>("/api/spotify/profile"),
          apiFetch<PlaylistsResponse>("/api/spotify/playlists"),
          apiFetch<LikedTracksResponse>("/api/spotify/liked?limit=50"),
        ]);

        setProfile(profileData);
        setPlaylists(playlistData.items);
        setLikedSongs(likedData.items);
        setContentMode("liked");
        setSelectedPlaylistId("");
        setSelectedPlaylistName("Lieblingssongs");
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "Spotify-Daten konnten nicht geladen werden.";
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    void loadData();
  }, [appAuthenticated, spotifyConnected]);

  const handlePlayTrack = useCallback(
    async (track: SpotifyTrack) => {
      if (!deviceId) {
        setError("Der Browser-Player ist noch nicht bereit.");
        return;
      }

      try {
        setError("");
        setSelectedTrackId(track.id);
        setProgressMs(0);
        setProgressDurationMs(track.duration_ms);
        setAutoAdvanceLock(false);
        setStatus(`Starte ${track.name} ...`);

        await apiFetch<OkResponse>("/api/spotify/player/play", {
          method: "POST",
          body: JSON.stringify({
            deviceId,
            uri: track.uri,
          }),
        });
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "Track konnte nicht gestartet werden.";
        setError(message);
      }
    },
    [deviceId],
  );

  const playNextTrackFromQueue = useCallback(async () => {
    if (!displayedTracks.length) {
      return;
    }

    const currentId =
      playerState?.track_window?.current_track?.id || selectedTrackId;

    if (!currentId) {
      if (displayedTracks[0]) {
        await handlePlayTrack(displayedTracks[0]);
      }
      return;
    }

    const currentIndex = displayedTracks.findIndex(
      (track) => track.id === currentId,
    );

    if (currentIndex === -1) {
      if (displayedTracks[0]) {
        await handlePlayTrack(displayedTracks[0]);
      }
      return;
    }

    const nextTrack = displayedTracks[currentIndex + 1];

    if (nextTrack) {
      await handlePlayTrack(nextTrack);
    } else {
      setStatus("Playlist beendet.");
    }
  }, [displayedTracks, handlePlayTrack, playerState, selectedTrackId]);

  useEffect(() => {
    let mounted = true;

    const initPlayer = async () => {
      if (!appAuthenticated || !spotifyConnected || playerRef.current) {
        return;
      }

      try {
        setPlayerBooting(true);
        await loadSpotifySdk();

        if (!window.Spotify || !mounted) {
          return;
        }

        const player: SpotifyWebPlayerInstance = new window.Spotify.Player({
          name: "Spoti Web Player",
          getOAuthToken: async (callback: (token: string) => void) => {
            try {
              const tokenResponse = await apiFetch<WebPlaybackTokenResponse>(
                "/api/spotify/web-playback-token",
              );
              callback(tokenResponse.accessToken);
            } catch (err) {
              const message =
                err instanceof Error
                  ? err.message
                  : "Token konnte nicht geladen werden.";
              setError(message);
            }
          },
          volume: 0.7,
        });

        player.addListener("ready", async (data: any) => {
          if (!mounted) {
            return;
          }

          const nextDeviceId =
            typeof data?.device_id === "string" ? data.device_id : "";

          setDeviceId(nextDeviceId);
          setPlayerReady(true);
          setStatus("Player bereit.");

          try {
            await apiFetch<OkResponse>("/api/spotify/player/transfer", {
              method: "POST",
              body: JSON.stringify({ deviceId: nextDeviceId }),
            });
          } catch (err) {
            const message =
              err instanceof Error
                ? err.message
                : "Player konnte nicht aktiviert werden.";
            setError(message);
          }
        });

        player.addListener("player_state_changed", (state: any) => {
          if (!mounted || !state) {
            return;
          }

          const nextState = state as PlayerState;
          setPlayerState(nextState);
          setProgressMs(nextState.position ?? 0);
          setProgressDurationMs(nextState.duration ?? 0);

          const currentId = nextState.track_window?.current_track?.id;
          if (currentId) {
            setSelectedTrackId(currentId);
            setAutoAdvanceLock(false);
          }
        });

        player.addListener("initialization_error", (data: any) => {
          setError(data?.message || "Initialisierungsfehler.");
        });

        player.addListener("authentication_error", (data: any) => {
          setError(data?.message || "Authentifizierungsfehler.");
        });

        player.addListener("account_error", (data: any) => {
          setError(data?.message || "Account-Fehler.");
        });

        player.addListener("playback_error", (data: any) => {
          setError(data?.message || "Playback-Fehler.");
        });

        const connected = await player.connect();

        if (!connected) {
          throw new Error("Spotify Player konnte nicht verbunden werden.");
        }

        playerRef.current = player;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Player-Start fehlgeschlagen.";
        setError(message);
      } finally {
        if (mounted) {
          setPlayerBooting(false);
        }
      }
    };

    void initPlayer();

    return () => {
      mounted = false;

      if (playerRef.current) {
        playerRef.current.disconnect();
        playerRef.current = null;
      }
    };
  }, [appAuthenticated, spotifyConnected]);

  useEffect(() => {
    if (!playerReady || !playerRef.current) {
      return;
    }

    const interval = window.setInterval(async () => {
      try {
        if (!playerRef.current) {
          return;
        }

        const state = await playerRef.current.getCurrentState();

        if (!state) {
          return;
        }

        setPlayerState(state);
        setProgressMs(state.position ?? 0);
        setProgressDurationMs(state.duration ?? 0);

        const currentId = state.track_window?.current_track?.id;
        if (currentId) {
          setSelectedTrackId(currentId);
        }

        const position = state.position ?? 0;
        const duration = state.duration ?? 0;

        const isTrackFinished =
          duration > 0 && position >= Math.max(duration - 1200, 0);

        if (isTrackFinished && !autoAdvanceLock) {
          setAutoAdvanceLock(true);
          await playNextTrackFromQueue();
        }

        if (
          !isTrackFinished &&
          autoAdvanceLock &&
          position < Math.max(duration - 3000, 0)
        ) {
          setAutoAdvanceLock(false);
        }
      } catch {
        // absichtlich leer
      }
    }, 1000);

    return () => {
      window.clearInterval(interval);
    };
  }, [playerReady, autoAdvanceLock, playNextTrackFromQueue]);

  const handleAppLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      setAuthLoading(true);
      setError("");

      await apiFetch<OkResponse>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ password }),
      });

      setAppAuthenticated(true);
      setPassword("");

      const spotifyStatus = await apiFetch<SpotifyStatusResponse>(
        "/api/spotify/status",
      );
      setSpotifyConnected(spotifyStatus.connected);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Login fehlgeschlagen.";
      setError(message);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleConnectSpotify = () => {
    window.location.href = "/api/spotify/login";
  };

  const handleDisconnectSpotify = async () => {
    try {
      setError("");
      await apiFetch<OkResponse>("/api/spotify/disconnect", { method: "POST" });

      if (playerRef.current) {
        playerRef.current.disconnect();
        playerRef.current = null;
      }

      setSpotifyConnected(false);
      setProfile(null);
      setPlaylists([]);
      setLikedSongs([]);
      setSearchResults([]);
      setPlaylistTracks([]);
      setSelectedPlaylistId("");
      setSelectedPlaylistName("Lieblingssongs");
      setContentMode("liked");
      setDeviceId("");
      setPlayerReady(false);
      setPlayerState(null);
      setSelectedTrackId("");
      setProgressMs(0);
      setProgressDurationMs(0);
      setAutoAdvanceLock(false);
      setStatus("Spotify-Verbindung entfernt.");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Trennen fehlgeschlagen.";
      setError(message);
    }
  };

  const handleAppLogout = async () => {
    try {
      await apiFetch<OkResponse>("/api/auth/logout", { method: "POST" });

      if (playerRef.current) {
        playerRef.current.disconnect();
        playerRef.current = null;
      }

      setAppAuthenticated(false);
      setSpotifyConnected(false);
      setProfile(null);
      setPlaylists([]);
      setLikedSongs([]);
      setSearchResults([]);
      setPlaylistTracks([]);
      setSelectedPlaylistId("");
      setSelectedPlaylistName("Lieblingssongs");
      setContentMode("liked");
      setDeviceId("");
      setPlayerReady(false);
      setPlayerState(null);
      setSelectedTrackId("");
      setProgressMs(0);
      setProgressDurationMs(0);
      setAutoAdvanceLock(false);
      setStatus("");
      setError("");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Logout fehlgeschlagen.";
      setError(message);
    }
  };

  const handleSearch = async () => {
    const trimmed = query.trim();

    if (!trimmed) {
      setSearchResults([]);
      setContentMode("search");
      setSelectedPlaylistId("");
      setSelectedPlaylistName("Suche");
      return;
    }

    try {
      setSearching(true);
      setError("");
      setContentMode("search");
      setSelectedPlaylistId("");
      setSelectedPlaylistName(`Suche: ${trimmed}`);

      const data = await apiFetch<SearchResponse>(
        `/api/spotify/search?q=${encodeURIComponent(trimmed)}`,
      );

      setSearchResults(data.tracks.items);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Suche fehlgeschlagen.";
      setError(message);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectLikedSongs = () => {
    setContentMode("liked");
    setSelectedPlaylistId("");
    setSelectedPlaylistName("Lieblingssongs");
  };

  const handleSelectPlaylist = async (playlist: SpotifyPlaylist) => {
    try {
      setPlaylistLoading(true);
      setError("");
      setContentMode("playlist");
      setSelectedPlaylistId(playlist.id);
      setSelectedPlaylistName(playlist.name);

      const data = await apiFetch<PlaylistTracksResponse>(
        `/api/spotify/playlists/${playlist.id}/tracks`,
      );

      const validTracks = data.items
        .map((item) => item.track)
        .filter((track): track is SpotifyTrack =>
          Boolean(track && track.id && track.uri),
        );

      setPlaylistTracks(validTracks);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Playlist konnte nicht geladen werden.";
      setError(message);
    } finally {
      setPlaylistLoading(false);
    }
  };

  const handleTogglePlay = async () => {
    if (!playerRef.current) {
      setError("Der Player ist noch nicht bereit.");
      return;
    }

    try {
      setError("");
      await playerRef.current.togglePlay();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Play/Pause fehlgeschlagen.";
      setError(message);
    }
  };

  const handleNext = async () => {
    if (!displayedTracks.length) {
      if (!playerRef.current) {
        setError("Der Player ist noch nicht bereit.");
        return;
      }

      try {
        setError("");
        await playerRef.current.nextTrack();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Weiter fehlgeschlagen.";
        setError(message);
      }
      return;
    }

    try {
      setError("");
      await playNextTrackFromQueue();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Weiter fehlgeschlagen.";
      setError(message);
    }
  };

  const handlePrevious = async () => {
    if (!displayedTracks.length) {
      if (!playerRef.current) {
        setError("Der Player ist noch nicht bereit.");
        return;
      }

      try {
        setError("");
        await playerRef.current.previousTrack();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Zurück fehlgeschlagen.";
        setError(message);
      }
      return;
    }

    const currentId =
      playerState?.track_window?.current_track?.id || selectedTrackId;

    if (!currentId) {
      return;
    }

    const currentIndex = displayedTracks.findIndex(
      (track) => track.id === currentId,
    );

    if (currentIndex <= 0) {
      const firstTrack = displayedTracks[0];
      if (firstTrack) {
        await handlePlayTrack(firstTrack);
      }
      return;
    }

    const previousTrack = displayedTracks[currentIndex - 1];
    if (previousTrack) {
      await handlePlayTrack(previousTrack);
    }
  };

  const handleSeek = async (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    if (!playerRef.current || !durationMs) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, offsetX / rect.width));
    const nextPosition = Math.floor(durationMs * ratio);

    try {
      await playerRef.current.seek(nextPosition);
      setProgressMs(nextPosition);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Spulen fehlgeschlagen.";
      setError(message);
    }
  };

  if (loading && !appAuthenticated) {
    return (
      <section className="spotify-page spotify-theme">
        <style>{spotifyPageStyles}</style>
        <div className="spotify-loading-screen">
          <div className="spotify-loading-card">
            <div className="spotify-brand-dot" />
            <p>Lädt Spotify Bereich...</p>
          </div>
        </div>
      </section>
    );
  }

  if (!appAuthenticated) {
    return (
      <section className="spotify-page spotify-theme">
        <style>{spotifyPageStyles}</style>

        <div className="spotify-auth-shell">
          <div className="spotify-auth-card">
            <div className="spotify-auth-logo">Spotify</div>
            <h1>Musikbereich entsperren</h1>
            <p>
              Private Playlists, Lieblingssongs und direkter Browser-Player.
            </p>

            <form onSubmit={handleAppLogin} className="spotify-auth-form">
              <label htmlFor="spotify-password">Passwort</label>
              <input
                id="spotify-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                placeholder="Passwort eingeben"
              />

              <button type="submit" disabled={authLoading}>
                {authLoading ? "Prüfe..." : "Einloggen"}
              </button>
            </form>

            {error ? <div className="spotify-banner error">{error}</div> : null}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="spotify-page spotify-theme">
      <style>{spotifyPageStyles}</style>

      <div className="spotify-app-shell">
        <aside className="spotify-left-sidebar">
          <div className="spotify-sidebar-block">
            <button className="spotify-nav-item active" type="button">
              <IconHome />
              <span>Startseite</span>
            </button>

            <button
              className="spotify-nav-item"
              type="button"
              onClick={() => {
                const searchInput = document.getElementById("spotify-search");
                searchInput?.focus();
              }}
            >
              <IconSearch />
              <span>Suchen</span>
            </button>
          </div>

          <div className="spotify-sidebar-library">
            <div className="spotify-library-header">
              <div className="spotify-library-title">
                <IconLibrary />
                <span>Deine Bibliothek</span>
              </div>
            </div>

            <button
              type="button"
              className={`spotify-library-liked ${
                contentMode === "liked" ? "active" : ""
              }`}
              onClick={handleSelectLikedSongs}
            >
              <div className="spotify-library-liked-cover">♥</div>
              <div className="spotify-library-liked-meta">
                <strong>Lieblingssongs</strong>
                <span>{likedSongs.length} Songs</span>
              </div>
            </button>

            <div className="spotify-playlist-scroll">
              {playlists.map((playlist) => {
                const active = selectedPlaylistId === playlist.id;
                return (
                  <button
                    key={playlist.id}
                    type="button"
                    className={`spotify-playlist-item ${active ? "active" : ""}`}
                    onClick={() => void handleSelectPlaylist(playlist)}
                  >
                    <div className="spotify-playlist-thumb">
                      {playlist.images?.[0]?.url ? (
                        <img src={playlist.images[0].url} alt={playlist.name} />
                      ) : (
                        <div className="spotify-playlist-thumb-fallback">♪</div>
                      )}
                    </div>

                    <div className="spotify-playlist-info">
                      <strong>{playlist.name}</strong>
                      <span>{getPlaylistTotal(playlist)} Tracks</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </aside>

        <main className="spotify-main-view">
          <div className="spotify-topbar">
            <div className="spotify-topbar-search">
              <IconSearch />
              <input
                id="spotify-search"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    void handleSearch();
                  }
                }}
                placeholder="Was möchtest du hören?"
              />
            </div>

            <div className="spotify-topbar-actions">
              {!spotifyConnected ? (
                <button
                  type="button"
                  className="spotify-pill-button spotify-green"
                  onClick={handleConnectSpotify}
                >
                  Spotify verbinden
                </button>
              ) : (
                <button
                  type="button"
                  className="spotify-pill-button"
                  onClick={handleDisconnectSpotify}
                >
                  Trennen
                </button>
              )}

              <button
                type="button"
                className="spotify-user-chip"
                onClick={handleAppLogout}
              >
                <div className="spotify-user-avatar">
                  {profile?.images?.[0]?.url ? (
                    <img
                      src={profile.images[0].url}
                      alt={profile.display_name || "Profil"}
                    />
                  ) : (
                    <span>
                      {profile?.display_name?.charAt(0).toUpperCase() || "S"}
                    </span>
                  )}
                </div>
                <span>{profile?.display_name || "User"}</span>
              </button>
            </div>
          </div>

          {error ? <div className="spotify-banner error">{error}</div> : null}
          {status ? (
            <div className="spotify-banner success">{status}</div>
          ) : null}

          {!spotifyConnected ? (
            <div className="spotify-disconnected-card">
              <h2>Spotify ist noch nicht verbunden</h2>
              <p>
                Verbinde deinen Account, damit Playlists, Suche und der Web
                Player verfügbar sind.
              </p>
              <button
                type="button"
                className="spotify-pill-button spotify-green"
                onClick={handleConnectSpotify}
              >
                Jetzt verbinden
              </button>
            </div>
          ) : (
            <>
              <section className="spotify-hero">
                <div className="spotify-hero-cover">
                  {contentMode === "playlist" &&
                  selectedPlaylist?.images?.[0]?.url ? (
                    <img
                      src={selectedPlaylist.images[0].url}
                      alt={selectedPlaylist.name}
                    />
                  ) : currentTrack?.album?.images?.[0]?.url ? (
                    <img
                      src={currentTrack.album.images[0].url}
                      alt={currentTrack.name || "Cover"}
                    />
                  ) : (
                    <div className="spotify-hero-cover-fallback">♪</div>
                  )}
                </div>

                <div className="spotify-hero-meta">
                  <span className="spotify-hero-type">
                    {contentMode === "playlist"
                      ? "Playlist"
                      : contentMode === "search"
                        ? "Suchergebnisse"
                        : "Playlist"}
                  </span>

                  <h1>{selectedPlaylistName}</h1>

                  <p className="spotify-hero-subline">
                    {contentMode === "playlist"
                      ? selectedPlaylist?.description ||
                        "Deine ausgewählte Playlist"
                      : contentMode === "search"
                        ? "Gefundene Songs basierend auf deiner Suche"
                        : "Deine gespeicherten Lieblingssongs"}
                  </p>

                  <div className="spotify-hero-stats">
                    <span>{profile?.display_name || "Spotify User"}</span>
                    <span>•</span>
                    <span>{displayedTracks.length} Tracks</span>
                    <span>•</span>
                    <span>
                      {playerBooting
                        ? "Player startet..."
                        : playerReady
                          ? "Web Player bereit"
                          : "Player nicht aktiv"}
                    </span>
                  </div>

                  <div className="spotify-hero-buttons">
                    <button
                      type="button"
                      className="spotify-big-play"
                      disabled={!playerReady || displayedTracks.length === 0}
                      onClick={() => void handlePlayTrack(displayedTracks[0])}
                    >
                      <IconPlay />
                      <span>Play</span>
                    </button>

                    <button
                      type="button"
                      className="spotify-secondary-action"
                      onClick={handleSelectLikedSongs}
                    >
                      Lieblingssongs
                    </button>

                    <button
                      type="button"
                      className="spotify-secondary-action"
                      onClick={() => void handleSearch()}
                      disabled={searching}
                    >
                      {searching ? "Suche..." : "Suchen"}
                    </button>
                  </div>
                </div>
              </section>

              <section className="spotify-content-panel">
                <div className="spotify-table-head">
                  <span>#</span>
                  <span>Titel</span>
                  <span>Album</span>
                  <span className="spotify-duration-head">
                    <IconClock />
                  </span>
                </div>

                <div className="spotify-track-list">
                  {playlistLoading ? (
                    <div className="spotify-empty-state">
                      Playlist wird geladen...
                    </div>
                  ) : displayedTracks.length === 0 ? (
                    <div className="spotify-empty-state">
                      Keine Einträge vorhanden.
                    </div>
                  ) : (
                    displayedTracks.map((track, index) => {
                      const active = selectedTrackId === track.id;

                      return (
                        <button
                          key={track.id}
                          type="button"
                          className={`spotify-track-row ${active ? "active" : ""}`}
                          onClick={() => void handlePlayTrack(track)}
                        >
                          <span className="spotify-track-index">
                            {active ? "▶" : index + 1}
                          </span>

                          <div className="spotify-track-main">
                            <div className="spotify-track-cover">
                              {track.album.images?.[0]?.url ? (
                                <img
                                  src={track.album.images[0].url}
                                  alt={track.name}
                                />
                              ) : (
                                <div className="spotify-track-cover-fallback">
                                  ♪
                                </div>
                              )}
                            </div>

                            <div className="spotify-track-meta">
                              <strong>{track.name}</strong>
                              <span>{getArtistNames(track)}</span>
                            </div>
                          </div>

                          <span className="spotify-track-album">
                            {track.album.name}
                          </span>

                          <span className="spotify-track-duration">
                            {formatDuration(track.duration_ms)}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              </section>
            </>
          )}
        </main>
      </div>

      <footer className="spotify-bottom-player">
        <div className="spotify-bottom-left">
          {currentTrack?.album?.images?.[0]?.url ? (
            <img
              src={currentTrack.album.images[0].url}
              alt={currentTrack.name || "Cover"}
              className="spotify-bottom-cover"
            />
          ) : (
            <div className="spotify-bottom-cover spotify-bottom-cover-fallback">
              ♪
            </div>
          )}

          <div className="spotify-bottom-track">
            <strong>{currentTrack?.name || "Noch nichts gestartet"}</strong>
            <span>
              {currentTrack?.artists?.map((artist) => artist.name).join(", ") ||
                "Wähle einen Track"}
            </span>
          </div>
        </div>

        <div className="spotify-bottom-center">
          <div className="spotify-player-controls">
            <button
              type="button"
              className="spotify-control-button"
              onClick={() => void handlePrevious()}
              disabled={!playerReady}
              aria-label="Vorheriger Track"
            >
              <IconPrevious />
            </button>

            <button
              type="button"
              className="spotify-main-control"
              onClick={() => void handleTogglePlay()}
              disabled={!playerReady}
              aria-label={isPaused ? "Play" : "Pause"}
            >
              {isPaused ? <IconPlay /> : <IconPause />}
            </button>

            <button
              type="button"
              className="spotify-control-button"
              onClick={() => void handleNext()}
              disabled={!playerReady}
              aria-label="Nächster Track"
            >
              <IconNext />
            </button>
          </div>

          <div className="spotify-progress-row">
            <span>{formatTimeMs(positionMs)}</span>

            <div
              className="spotify-progress-bar"
              onClick={(event) => void handleSeek(event)}
              role="button"
              tabIndex={0}
            >
              <div
                className="spotify-progress-fill"
                style={{ width: `${progressPercent}%` }}
              />
            </div>

            <span>{formatTimeMs(durationMs)}</span>
          </div>
        </div>

        <div className="spotify-bottom-right">
          <span>
            {playerBooting
              ? "Startet..."
              : playerReady
                ? "Bereit"
                : "Nicht verbunden"}
          </span>
          <span className="spotify-device-pill">
            {deviceId || "Kein Device"}
          </span>
        </div>
      </footer>
    </section>
  );
}

const spotifyPageStyles = `
.spotify-theme {
  --spotify-black: #000000;
  --spotify-dark: #121212;
  --spotify-panel: #181818;
  --spotify-panel-soft: #1f1f1f;
  --spotify-panel-hover: #2a2a2a;
  --spotify-border: rgba(255, 255, 255, 0.08);
  --spotify-text: #ffffff;
  --spotify-subtext: #b3b3b3;
  --spotify-subtext-soft: #8a8a8a;
  --spotify-green: #1db954;
  --spotify-green-hover: #1ed760;
  --spotify-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
  color: var(--spotify-text);
  min-height: 100vh;
  background: var(--spotify-black);
  font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  padding-bottom: 96px;
}

.spotify-page * {
  box-sizing: border-box;
}

.spotify-page button,
.spotify-page input {
  font: inherit;
}

.spotify-loading-screen,
.spotify-auth-shell {
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 24px;
  background:
    radial-gradient(circle at top left, rgba(29, 185, 84, 0.18), transparent 30%),
    radial-gradient(circle at bottom right, rgba(80, 40, 140, 0.22), transparent 35%),
    linear-gradient(180deg, #111 0%, #000 100%);
}

.spotify-loading-card,
.spotify-auth-card {
  width: 100%;
  max-width: 420px;
  background: rgba(24, 24, 24, 0.96);
  border: 1px solid var(--spotify-border);
  border-radius: 24px;
  padding: 32px;
  box-shadow: var(--spotify-shadow);
}

.spotify-brand-dot {
  width: 48px;
  height: 48px;
  border-radius: 999px;
  background: var(--spotify-green);
  margin-bottom: 20px;
}

.spotify-auth-logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(29, 185, 84, 0.14);
  color: #d7ffe5;
  font-weight: 700;
  margin-bottom: 16px;
}

.spotify-auth-card h1 {
  margin: 0 0 12px;
  font-size: 32px;
  line-height: 1.1;
}

.spotify-auth-card p {
  margin: 0 0 24px;
  color: var(--spotify-subtext);
}

.spotify-auth-form {
  display: grid;
  gap: 12px;
}

.spotify-auth-form label {
  font-size: 14px;
  color: var(--spotify-subtext);
}

.spotify-auth-form input {
  width: 100%;
  height: 48px;
  border: 1px solid var(--spotify-border);
  background: #121212;
  color: #fff;
  border-radius: 12px;
  padding: 0 14px;
  outline: none;
}

.spotify-auth-form input:focus {
  border-color: rgba(29, 185, 84, 0.7);
}

.spotify-auth-form button,
.spotify-pill-button,
.spotify-secondary-action,
.spotify-big-play,
.spotify-user-chip,
.spotify-control-button,
.spotify-main-control,
.spotify-nav-item,
.spotify-library-liked,
.spotify-playlist-item,
.spotify-track-row {
  border: none;
  cursor: pointer;
}

.spotify-auth-form button {
  height: 48px;
  border-radius: 999px;
  background: var(--spotify-green);
  color: #000;
  font-weight: 800;
}

.spotify-auth-form button:hover {
  background: var(--spotify-green-hover);
}

.spotify-auth-form button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spotify-app-shell {
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 8px;
  min-height: calc(100vh - 100px);
  padding: 8px;
}

.spotify-left-sidebar {
  min-height: 100%;
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 8px;
}

.spotify-sidebar-block,
.spotify-sidebar-library {
  background: var(--spotify-dark);
  border-radius: 12px;
  overflow: hidden;
}

.spotify-sidebar-block {
  padding: 12px;
}

.spotify-nav-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px;
  border-radius: 10px;
  background: transparent;
  color: var(--spotify-subtext);
  font-weight: 700;
  transition: background 0.2s ease, color 0.2s ease;
}

.spotify-nav-item:hover,
.spotify-nav-item.active {
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
}

.spotify-svg-icon {
  width: 20px;
  height: 20px;
  flex: 0 0 auto;
}

.spotify-sidebar-library {
  padding: 14px 10px 10px;
  display: grid;
  grid-template-rows: auto auto 1fr;
  min-height: 0;
}

.spotify-library-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px 14px;
}

.spotify-library-title {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--spotify-subtext);
  font-weight: 700;
}

.spotify-library-liked,
.spotify-playlist-item {
  width: 100%;
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  padding: 8px;
  background: transparent;
  color: #fff;
  border-radius: 10px;
  text-align: left;
}

.spotify-library-liked:hover,
.spotify-playlist-item:hover,
.spotify-library-liked.active,
.spotify-playlist-item.active {
  background: rgba(255, 255, 255, 0.08);
}

.spotify-library-liked-cover,
.spotify-playlist-thumb,
.spotify-playlist-thumb-fallback {
  width: 56px;
  height: 56px;
  border-radius: 6px;
}

.spotify-library-liked-cover {
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, #450af5 0%, #c4efd9 100%);
  font-size: 24px;
}

.spotify-playlist-thumb {
  overflow: hidden;
  background: #242424;
}

.spotify-playlist-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.spotify-playlist-thumb-fallback {
  display: grid;
  place-items: center;
  background: #2a2a2a;
  color: #fff;
  font-size: 20px;
}

.spotify-library-liked-meta,
.spotify-playlist-info {
  min-width: 0;
  display: grid;
  gap: 4px;
}

.spotify-library-liked-meta strong,
.spotify-playlist-info strong {
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.spotify-library-liked-meta span,
.spotify-playlist-info span {
  color: var(--spotify-subtext);
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.spotify-playlist-scroll {
  min-height: 0;
  overflow-y: auto;
  padding-top: 8px;
}

.spotify-playlist-scroll::-webkit-scrollbar {
  width: 10px;
}

.spotify-playlist-scroll::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.18);
}

.spotify-main-view {
  min-width: 0;
  background:
    linear-gradient(180deg, rgba(70, 30, 90, 0.65) 0%, rgba(18, 18, 18, 0.95) 240px),
    var(--spotify-dark);
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.spotify-topbar {
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px 8px;
  backdrop-filter: blur(12px);
}

.spotify-topbar-search {
  flex: 1;
  max-width: 440px;
  height: 48px;
  display: flex;
  align-items: center;
  gap: 12px;
  background: #ffffff;
  border-radius: 999px;
  padding: 0 16px;
  color: #121212;
}

.spotify-topbar-search input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: #121212;
}

.spotify-topbar-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.spotify-pill-button {
  height: 40px;
  padding: 0 18px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-weight: 700;
}

.spotify-pill-button:hover {
  transform: scale(1.03);
}

.spotify-pill-button.spotify-green {
  background: var(--spotify-green);
  color: #000;
}

.spotify-pill-button.spotify-green:hover {
  background: var(--spotify-green-hover);
}

.spotify-user-chip {
  height: 40px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  padding: 0 12px 0 6px;
  border-radius: 999px;
  font-weight: 700;
}

.spotify-user-avatar {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  overflow: hidden;
  background: #2a2a2a;
  display: grid;
  place-items: center;
  font-size: 12px;
}

.spotify-user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.spotify-banner {
  margin: 8px 24px 0;
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 14px;
}

.spotify-banner.error {
  background: rgba(255, 82, 82, 0.12);
  border: 1px solid rgba(255, 82, 82, 0.25);
  color: #ffd7d7;
}

.spotify-banner.success {
  background: rgba(29, 185, 84, 0.12);
  border: 1px solid rgba(29, 185, 84, 0.25);
  color: #d6ffe3;
}

.spotify-disconnected-card {
  margin: 24px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--spotify-border);
  border-radius: 20px;
  padding: 28px;
}

.spotify-disconnected-card h2 {
  margin: 0 0 10px;
  font-size: 28px;
}

.spotify-disconnected-card p {
  margin: 0 0 18px;
  color: var(--spotify-subtext);
}

.spotify-hero {
  padding: 24px;
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  gap: 24px;
  align-items: end;
}

.spotify-hero-cover,
.spotify-hero-cover-fallback {
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--spotify-shadow);
  background: #202020;
}

.spotify-hero-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.spotify-hero-cover-fallback {
  display: grid;
  place-items: center;
  font-size: 72px;
  color: #fff;
}

.spotify-hero-type {
  display: inline-block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 700;
}

.spotify-hero-meta h1 {
  margin: 0;
  font-size: clamp(42px, 6vw, 84px);
  line-height: 0.95;
  letter-spacing: -0.03em;
}

.spotify-hero-subline {
  margin: 16px 0 14px;
  color: #e6e6e6;
  max-width: 820px;
}

.spotify-hero-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  color: var(--spotify-subtext);
  font-size: 14px;
  font-weight: 600;
}

.spotify-hero-buttons {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 24px;
  flex-wrap: wrap;
}

.spotify-big-play {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 56px;
  padding: 0 28px;
  border-radius: 999px;
  background: var(--spotify-green);
  color: #000;
  font-weight: 800;
}

.spotify-big-play:hover:not(:disabled) {
  background: var(--spotify-green-hover);
  transform: scale(1.03);
}

.spotify-secondary-action {
  height: 44px;
  padding: 0 16px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-weight: 700;
}

.spotify-secondary-action:hover {
  background: rgba(255, 255, 255, 0.12);
}

.spotify-big-play:disabled,
.spotify-secondary-action:disabled,
.spotify-control-button:disabled,
.spotify-main-control:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.spotify-content-panel {
  min-height: 0;
  padding: 8px 24px 24px;
}

.spotify-table-head {
  display: grid;
  grid-template-columns: 48px minmax(0, 1.8fr) minmax(0, 1.1fr) 100px;
  gap: 16px;
  align-items: center;
  padding: 0 16px 12px;
  margin-bottom: 8px;
  border-bottom: 1px solid var(--spotify-border);
  color: var(--spotify-subtext);
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.spotify-duration-head {
  display: inline-flex;
  justify-content: flex-end;
}

.spotify-track-list {
  display: grid;
}

.spotify-track-row {
  width: 100%;
  display: grid;
  grid-template-columns: 48px minmax(0, 1.8fr) minmax(0, 1.1fr) 100px;
  gap: 16px;
  align-items: center;
  background: transparent;
  color: #fff;
  text-align: left;
  padding: 10px 16px;
  border-radius: 8px;
}

.spotify-track-row:hover {
  background: rgba(255, 255, 255, 0.09);
}

.spotify-track-row.active {
  background: rgba(255, 255, 255, 0.12);
}

.spotify-track-index {
  color: var(--spotify-subtext);
  font-size: 14px;
}

.spotify-track-row.active .spotify-track-index,
.spotify-track-row.active .spotify-track-meta strong {
  color: var(--spotify-green);
}

.spotify-track-main {
  min-width: 0;
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr);
  gap: 12px;
  align-items: center;
}

.spotify-track-cover,
.spotify-track-cover-fallback {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  overflow: hidden;
  background: #2a2a2a;
}

.spotify-track-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.spotify-track-cover-fallback {
  display: grid;
  place-items: center;
}

.spotify-track-meta {
  min-width: 0;
  display: grid;
  gap: 4px;
}

.spotify-track-meta strong,
.spotify-track-meta span,
.spotify-track-album {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.spotify-track-meta strong {
  font-size: 15px;
  font-weight: 700;
}

.spotify-track-meta span,
.spotify-track-album,
.spotify-track-duration {
  color: var(--spotify-subtext);
  font-size: 14px;
}

.spotify-track-duration {
  text-align: right;
}

.spotify-empty-state {
  margin-top: 12px;
  padding: 30px 20px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.04);
  color: var(--spotify-subtext);
  text-align: center;
}

.spotify-bottom-player {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  height: 84px;
  background: #181818;
  border-top: 1px solid var(--spotify-border);
  display: grid;
  grid-template-columns: minmax(240px, 1fr) minmax(320px, 1.2fr) minmax(220px, 1fr);
  align-items: center;
  gap: 16px;
  padding: 0 16px;
}

.spotify-bottom-left,
.spotify-bottom-center,
.spotify-bottom-right {
  min-width: 0;
}

.spotify-bottom-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.spotify-bottom-cover,
.spotify-bottom-cover-fallback {
  width: 56px;
  height: 56px;
  border-radius: 6px;
  background: #2a2a2a;
  flex: 0 0 auto;
}

.spotify-bottom-cover {
  object-fit: cover;
}

.spotify-bottom-cover-fallback {
  display: grid;
  place-items: center;
}

.spotify-bottom-track {
  min-width: 0;
  display: grid;
  gap: 4px;
}

.spotify-bottom-track strong,
.spotify-bottom-track span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.spotify-bottom-track strong {
  font-size: 14px;
}

.spotify-bottom-track span {
  color: var(--spotify-subtext);
  font-size: 12px;
}

.spotify-bottom-center {
  display: grid;
  justify-items: center;
  gap: 8px;
}

.spotify-player-controls {
  display: flex;
  align-items: center;
  gap: 16px;
}

.spotify-control-button,
.spotify-main-control {
  display: grid;
  place-items: center;
  background: transparent;
  color: #fff;
}

.spotify-control-button {
  width: 32px;
  height: 32px;
  color: var(--spotify-subtext);
}

.spotify-control-button:hover:not(:disabled) {
  color: #fff;
  transform: scale(1.06);
}

.spotify-main-control {
  width: 40px;
  height: 40px;
  border-radius: 999px;
  background: #fff;
  color: #000;
}

.spotify-main-control:hover:not(:disabled) {
  transform: scale(1.06);
}

.spotify-progress-row {
  width: min(100%, 560px);
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr) 48px;
  gap: 10px;
  align-items: center;
  color: var(--spotify-subtext);
  font-size: 12px;
}

.spotify-progress-bar {
  position: relative;
  height: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  overflow: hidden;
  cursor: pointer;
}

.spotify-progress-fill {
  height: 100%;
  border-radius: inherit;
  background: #fff;
}

.spotify-progress-bar:hover .spotify-progress-fill {
  background: var(--spotify-green);
}

.spotify-bottom-right {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  color: var(--spotify-subtext);
  font-size: 12px;
}

.spotify-device-pill {
  max-width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
}

@media (max-width: 1180px) {
  .spotify-app-shell {
    grid-template-columns: 280px minmax(0, 1fr);
  }

  .spotify-hero {
    grid-template-columns: 180px minmax(0, 1fr);
  }

  .spotify-table-head,
  .spotify-track-row {
    grid-template-columns: 40px minmax(0, 1.6fr) minmax(0, 1fr) 80px;
  }
}

@media (max-width: 920px) {
  .spotify-app-shell {
    grid-template-columns: 1fr;
  }

  .spotify-left-sidebar {
    display: none;
  }

  .spotify-bottom-player {
    grid-template-columns: 1fr;
    height: auto;
    padding: 14px 16px;
    gap: 14px;
  }

  .spotify-bottom-right {
    justify-content: center;
  }
}

@media (max-width: 720px) {
  .spotify-topbar {
    flex-direction: column;
    align-items: stretch;
  }

  .spotify-topbar-search {
    max-width: 100%;
  }

  .spotify-topbar-actions {
    justify-content: space-between;
  }

  .spotify-hero {
    grid-template-columns: 1fr;
    align-items: start;
  }

  .spotify-hero-cover,
  .spotify-hero-cover-fallback {
    width: min(240px, 100%);
  }

  .spotify-hero-meta h1 {
    font-size: 44px;
  }

  .spotify-table-head {
    display: none;
  }

  .spotify-track-row {
    grid-template-columns: 28px minmax(0, 1fr) 72px;
  }

  .spotify-track-album {
    display: none;
  }
}
`;

export default SpotifyPage;
